import React, { useEffect } from "react";
import NavbarPage from "../../components/Navbar/Navbar_Page";
import Footer from "../../components/Footer/footer";
import "../Index10/Index10.css";
import "./Legal.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-ten legal-page">
      <NavbarPage navItems={[]} navClass="" />
      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: March 28, 2026</p>

        <p>
          This Privacy Policy explains how CmSoftware ("Company", "we", "us", or "our") collects,
          uses, shares, and protects your personal information when you use the Public Backlog platform
          ("Service") at app.publicbacklog.com. By using the Service, you agree to the collection
          and use of information in accordance with this policy.
        </p>

        <hr />

        <h2>1. Information We Collect</h2>

        <h3>1.1 Account Information</h3>
        <p>When you create an account, we collect:</p>
        <ul>
          <li><strong>Required:</strong> Email address, first name, last name, and password (stored in hashed form).</li>
          <li><strong>Optional:</strong> Phone number, profile avatar image.</li>
          <li><strong>Via Google OAuth:</strong> If you sign up with Google, we receive your email address, first name, and last name from Google.</li>
          <li><strong>Via SSO (OIDC):</strong> If your organization uses SSO (Okta, Microsoft Entra ID, Auth0, Google Workspace, or other OIDC providers), we receive your email address and external identity identifier from your identity provider.</li>
        </ul>

        <h3>1.2 Organization and Team Information</h3>
        <p>When you create or join an Organization, we collect:</p>
        <ul>
          <li>Organization name and configuration settings.</li>
          <li>Organization User profiles (email, name, role, join date).</li>
          <li>Invitation records (invitee email address, invitation code, expiration).</li>
        </ul>

        <h3>1.3 User-Generated Content</h3>
        <p>When you use the Service, you may submit:</p>
        <ul>
          <li>Tickets (feedback, bug reports, feature requests, ideas, and questions) including title, description, and custom field values.</li>
          <li>Comments and replies on tickets.</li>
          <li>Votes on tickets.</li>
          <li>Internal notes (visible only to Organization administrators).</li>
          <li>Changelog entries.</li>
          <li>Roadmap configurations.</li>
          <li>File attachments (images and documents uploaded to tickets or profiles).</li>
        </ul>

        <h3>1.4 Automatically Collected Information</h3>
        <p>When you visit the Service, we automatically collect:</p>
        <ul>
          <li><strong>IP address</strong> (IPv4 or IPv6).</li>
          <li><strong>User agent</strong> (browser type, version, and operating system).</li>
          <li><strong>HTTP referrer</strong> (the page that linked you to us).</li>
          <li><strong>Pages visited</strong> (request paths within the Service).</li>
          <li><strong>Timestamps</strong> of visits.</li>
        </ul>
        <p>
          This data is collected for analytics, security, and service improvement purposes and is
          not linked to your user account.
        </p>

        <h3>1.5 Cookies and Local Storage</h3>
        <p>We use the following browser storage mechanisms:</p>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>AuthToken</td>
              <td>HttpOnly Cookie</td>
              <td>Authentication session (JWT token). Expires after 7 or 30 days.</td>
            </tr>
            <tr>
              <td>OrganizationAuthToken</td>
              <td>HttpOnly Cookie</td>
              <td>Organization-scoped authentication session.</td>
            </tr>
            <tr>
              <td>pb_theme</td>
              <td>localStorage</td>
              <td>Your display theme preference (light, dark, or system).</td>
            </tr>
            <tr>
              <td>pb_recent_orgs</td>
              <td>localStorage</td>
              <td>Recently accessed organizations for quick navigation.</td>
            </tr>
            <tr>
              <td>pb_tutorial</td>
              <td>localStorage</td>
              <td>Onboarding tutorial progress.</td>
            </tr>
          </tbody>
        </table>
        <p>
          Our authentication cookies are <strong>HttpOnly</strong> and <strong>Secure</strong>, meaning
          they cannot be accessed by JavaScript and are only transmitted over HTTPS. We do not use
          third-party advertising or tracking cookies.
        </p>

        <h3>1.6 Payment Information</h3>
        <p>
          If you subscribe to a paid plan, payment is processed by <strong>Stripe</strong>. We do not
          store your credit card number or banking details. We store only:
        </p>
        <ul>
          <li>Stripe customer ID (a reference identifier).</li>
          <li>Subscription plan and status.</li>
          <li>Invoice numbers.</li>
        </ul>
        <p>
          Stripe's handling of your payment data is governed by the{" "}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>.
        </p>

        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li><strong>Provide the Service:</strong> Create and manage your account, authenticate sessions, and deliver the core feedback management features.</li>
          <li><strong>Process payments:</strong> Manage subscriptions and billing through Stripe.</li>
          <li><strong>Send transactional emails:</strong> Account verification, password resets, organization invitations, and notification digests.</li>
          <li><strong>Deliver notifications:</strong> In-app notifications for ticket status changes, comments, and other activity you subscribe to.</li>
          <li><strong>Operate integrations:</strong> Sync your data with third-party platforms you have explicitly connected.</li>
          <li><strong>AI-powered features:</strong> Analyze ticket content for spam detection and relevancy scoring to help you prioritize feedback.</li>
          <li><strong>Improve the Service:</strong> Analyze usage patterns, diagnose errors, and improve performance and features.</li>
          <li><strong>Ensure security:</strong> Detect and prevent fraud, abuse, and unauthorized access.</li>
        </ul>

        <h2>3. How We Share Your Information</h2>
        <p>
          We do not sell your personal information. We share your data only in the following circumstances:
        </p>

        <h3>3.1 Third-Party Service Providers</h3>
        <p>We use the following service providers to operate the Service:</p>
        <table>
          <thead>
            <tr>
              <th>Provider</th>
              <th>Purpose</th>
              <th>Data Shared</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Stripe</td>
              <td>Payment processing</td>
              <td>Customer ID, subscription data</td>
            </tr>
            <tr>
              <td>SendGrid</td>
              <td>Transactional email</td>
              <td>Email addresses, names, email content</td>
            </tr>
            <tr>
              <td>Sentry</td>
              <td>Error monitoring</td>
              <td>Error logs, request data, user context for debugging</td>
            </tr>
            <tr>
              <td>Amazon S3</td>
              <td>File storage</td>
              <td>Uploaded files (avatars, ticket attachments)</td>
            </tr>
          </tbody>
        </table>

        <h3>3.2 AI Processing</h3>
        <p>
          Ticket content may be sent to AI services (currently Groq) for spam detection and
          relevancy scoring. This processing is automated and used solely to help you filter and
          prioritize feedback. No personal account information is sent — only ticket content (title
          and description).
        </p>

        <h3>3.3 User-Enabled Integrations</h3>
        <p>
          When you or your Organization administrator connects a third-party integration, ticket data
          (titles, descriptions, status, comments) is shared with that platform. Currently supported
          integrations include:
        </p>
        <ul>
          <li><strong>Project Management:</strong> GitHub, Jira, Asana, Linear, Azure DevOps, ClickUp, Notion</li>
          <li><strong>Communication:</strong> Slack, Microsoft Teams, Discord</li>
          <li><strong>Support:</strong> Intercom, Zendesk, Help Scout, Freshdesk</li>
          <li><strong>CRM:</strong> Salesforce, HubSpot</li>
          <li><strong>Automation:</strong> Zapier, Segment, Outgoing Webhooks</li>
        </ul>
        <p>
          Integration connections are initiated by you and can be disconnected at any time.
          Each integration platform has its own privacy policy governing how they handle your data.
          Integration credentials (OAuth tokens and API keys) are stored in encrypted form and used
          solely to maintain the connection.
        </p>

        <h3>3.4 Review Sources</h3>
        <p>
          If you enable review aggregation, we poll public review platforms (App Store, Trustpilot)
          for reviews related to your product and import them as tickets. This involves reading
          publicly available review data — we do not share your data with these platforms.
        </p>

        <h3>3.5 Legal and Safety</h3>
        <p>
          We may disclose your information if required by law, legal process, or government request,
          or if we believe in good faith that disclosure is necessary to protect the rights, property,
          or safety of CmSoftware, our users, or the public.
        </p>

        <h2>4. Data Retention</h2>
        <ul>
          <li><strong>Account data:</strong> Retained as long as your account is active. Upon account deletion, personal data is soft-deleted (marked as deleted) and may be permanently purged after a retention period.</li>
          <li><strong>User-generated content:</strong> Retained as long as the associated Organization and Backlog exist. Deleted content is soft-deleted and may be retained for a period before permanent removal.</li>
          <li><strong>Visit logs:</strong> IP addresses and page visit data is retained for analytics and security purposes.</li>
          <li><strong>Audit trails:</strong> Activity logs (ticket changes, status updates) are retained for the lifetime of the Organization for accountability and compliance purposes.</li>
          <li><strong>Payment records:</strong> Subscription and invoice records are retained as required for accounting and legal obligations.</li>
        </ul>

        <h2>5. Data Security</h2>
        <p>We implement the following security measures to protect your data:</p>
        <ul>
          <li>Passwords are cryptographically hashed before storage — we never store plaintext passwords.</li>
          <li>Authentication uses secure, HttpOnly, encrypted JWT tokens transmitted only over HTTPS.</li>
          <li>Integration credentials (OAuth tokens, API keys) are encrypted at rest using application-level encryption.</li>
          <li>All data in transit is encrypted via TLS/HTTPS.</li>
          <li>Role-based access control limits data access within Organizations.</li>
          <li>Webhook payloads are signed using HMAC-SHA256 for integrity verification.</li>
        </ul>
        <p>
          While we take reasonable measures to protect your data, no method of electronic storage
          or transmission is 100% secure. We cannot guarantee absolute security.
        </p>

        <h2>6. Your Rights</h2>
        <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
          <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).</li>
          <li><strong>Portability:</strong> Request your data in a structured, machine-readable format.</li>
          <li><strong>Objection:</strong> Object to the processing of your data for certain purposes.</li>
          <li><strong>Restriction:</strong> Request that we limit the processing of your data.</li>
          <li><strong>Withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:support@publicbacklog.com">support@publicbacklog.com</a>. We will respond
          to your request within 30 days.
        </p>

        <h3>6.1 California Residents (CCPA)</h3>
        <p>
          If you are a California resident, you have the right to know what personal information we
          collect, request its deletion, and opt out of its sale. We do not sell personal information.
          To make a request, contact us at the email above.
        </p>

        <h3>6.2 European Economic Area Residents (GDPR)</h3>
        <p>
          If you are in the EEA, our legal bases for processing your data include: performance of a
          contract (providing the Service), legitimate interests (improving and securing the Service),
          and consent (where applicable). You have the right to lodge a complaint with your local
          data protection authority.
        </p>

        <h2>7. International Data Transfers</h2>
        <p>
          Your data may be processed and stored in the United States or other countries where our
          service providers operate. By using the Service, you consent to the transfer of your data
          to these jurisdictions, which may have different data protection laws than your country of
          residence.
        </p>

        <h2>8. Children's Privacy</h2>
        <p>
          The Service is not intended for anyone under the age of 18. We do not knowingly collect
          personal information from individuals under 18. If we learn that we have collected personal
          data from anyone under 18, we will take steps to delete that information promptly. If you
          believe a minor has provided us with personal data, please contact us.
        </p>

        <h2>9. Third-Party Links</h2>
        <p>
          The Service may contain links to third-party websites or services. We are not responsible
          for the privacy practices or content of these third parties. We encourage you to review
          their privacy policies before providing them with your information.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we will
          notify you by email or through a notice on the Service and update the "Last updated" date
          at the top of this page. Your continued use of the Service after changes become effective
          constitutes your acceptance of the revised policy.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our
          data practices, please contact us at:
        </p>
        <ul>
          <li>Email: <a href="mailto:support@publicbacklog.com">support@publicbacklog.com</a></li>
          <li>Website: <a href="https://publicbacklog.com">publicbacklog.com</a></li>
        </ul>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
